import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Stack,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    Spinner,
    SpinnerSize,
    DefaultButton,
    PrimaryButton,
    Label,
    Link,
    Checkbox,
    Icon,
    Text
} from "@fluentui/react";
import { getPreprocessDocumentsDownloadURL, getListFilesAllPages, getContainer } from "../../../api/admin/api_admin";
import { ContainerInfo, FileInfo } from "../../../api/admin/models_admin";
import { AdminLabel, CellContent, ErrorDialog, formatDateTime, formatFileSize, ConfirmDialog, naturalCollator, filterByKeyword, ListFilterBox, FilePickButton } from "../components";
import { getDownloadURL } from "../../../api";
import { ApplyChangesModal, ApplyChangesResult } from "./apply_changes_modal";
import {
    PendingOps,
    FileRow,
    buildApplyPlan,
    buildFileRows,
    cancelPending,
    fileRowState,
    fileRowStateOrder,
    fileRowSize,
    fileRowType,
    pendingOpKeyOfFile,
    queueDelete,
    queueUpload
} from "./pending_ops";
import { IndexerLockMessageBar, isLockedByIndexer } from "./indexer_lock";
import { useBeforeUnloadGuard } from "../../../hooks/useBeforeUnloadGuard";
import { union, difference } from "lodash-es";

// 保留中の操作を一覧上で示すバッジ
const pendingBadgeStyle = (color: string) => ({
    fontSize: 11,
    fontWeight: 600,
    color,
    padding: '2px 8px',
    borderRadius: 12,
    backgroundColor: color + '1a',
    whiteSpace: 'nowrap' as const
})

export const ListFiles = () => {
    const { container_seq } = useParams<{ container_seq?: string }>();
    const containerSeqNum = container_seq ? parseInt(container_seq) : undefined;

    const [files, setFiles] = useState<FileInfo[]>([]);
    const [container, setContainer] = useState<ContainerInfo|null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // ファイルはページ単位で全件取得するため、進捗を出して待ち時間の目安を示す
    const [loadProgress, setLoadProgress] = useState<{ loaded: number, total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // 「編集を完了」まで実行を保留している操作。キーは表示上のファイル名
    const [pendingOps, setPendingOps] = useState<PendingOps>(new Map())
    // 選択状態。新規アップロード予定の行にはseqが無いため、ファイル名で保持する
    const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set())

    // ソート状態
    const [sortedColumn, setSortedColumn] = useState<string | null>(null);
    const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);

    // 保留中の操作の実行モーダル
    const [applying, setApplying] = useState(false)

    // 絞り込み文字列。DetailsListを仮想化しているためブラウザのページ内検索が使えない
    // 絞り込み → 並び替えの順に適用する。拡張子でも引けるよう、表示上のファイル名を検索対象にする
    const [fileNameFilter, setFileNameFilter] = useState<string>('');

    const rows = buildFileRows(files, pendingOps)
    const filteredRows = filterByKeyword(rows, fileNameFilter, row => row.key);

    const checkedFilteredCount = filteredRows.filter(row => checkedKeys.has(row.key)).length;
    const allFilteredChecked = filteredRows.length > 0 && checkedFilteredCount === filteredRows.length;
    const someFilteredChecked = checkedFilteredCount > 0 && !allFilteredChecked;

    // 反映状態を取得できなかった場合はnull。?? で潰すとnullがfalse(編集可)になるため、読み込み前だけをfalseにする
    const isIndexerRunning = container === null ? false : container.is_indexer_running
    const isEditable = !isLockedByIndexer(isIndexerRunning) && !applying

    // 保留中の操作がある場合のみ「編集を完了」できる。
    // 未反映の変更だけが残っている場合の反映は、カテゴリ設定の「今すぐ反映する」で行う
    const canApply = 0 < pendingOps.size

    // 保留中の操作を実行しないままタブを閉じられると変更が失われるため、離脱前に確認する
    const { closeWindow } = useBeforeUnloadGuard(0 < pendingOps.size);

    useEffect(() => {
        if (container_seq === undefined) return;
        initPage();
    }, [container_seq]);

    const initPage = async () => {
        if (containerSeqNum === undefined) return;
        setLoading(true);
        setLoadProgress(null);
        setError(null);
        try {
            // コンテナ取得
            const containerInfo = await getContainer(containerSeqNum);
            if ("error" in containerInfo) {
                setError(containerInfo.error);
                return;
            } else {
                setContainer(containerInfo)
            }

            // 列ごとのソートは全件に対して行う必要があるため、部分ロードにはせず全件を読み切る
            const data = await getListFilesAllPages(containerSeqNum, (loaded, total) => {
                setLoadProgress({ loaded, total });
            });
            if ("error" in data) {
                setError(data.error);
            } else {
                setFiles(data.files);
            }
        } catch (err) {
            setError("ファイル情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    // 選択中のファイルを削除予定にする
    const handleDeleteClick = () => {
        setPendingOps(current => {
            let next = current
            for (const row of rows) {
                if (!checkedKeys.has(row.key)) continue
                next = queueDelete(next, row.key, row.file)
            }
            return next
        })
        setCheckedKeys(new Set())
    };

    // ファイルをアップロード予定にする。実際のアップロードは「編集を完了」で行う
    const handleAddFile = (fileList: FileList | null) => {
        if (fileList === null) return
        setPendingOps(current => {
            let next = current
            for (const file of Array.from(fileList)) {
                next = queueUpload(next, file)
            }
            return next
        })
    }

    const handleCancelPending = (key: string) => {
        setPendingOps(current => cancelPending(current, key))
    }

    const handleApplyFinished = async (result: ApplyChangesResult) => {
        setApplying(false)

        // 実行に入らずキャンセルされた場合は、保留をそのまま残して編集を続けられるようにする
        if (!result.executed) return

        setCheckedKeys(new Set())

        const hasFailure = 0 < result.failedDeletions.length || 0 < result.failedUploads.length
        if (result.indexerStarted && !hasFailure) {
            setPendingOps(new Map());
            // 保留は実行し終えたため、stateの反映(次の描画)を待たずに確認なしで閉じる
            if (closeWindow()) return
            // スクリプトから開いたタブでなければ閉じられないため、結果を示して一覧を最新化する
            setSuccessMessage(`${result.message}
このタブは閉じて構いません。`)
            await initPage()
            return
        }

        // 実行できなかった操作は保留へ戻し、やり直せるようにする。
        // 失敗が残っている間はタブを閉じずに、一覧を最新化して編集を継続できるようにする
        await initPage()
        setPendingOps(() => {
            let next: PendingOps = new Map()
            for (const file of result.failedDeletions) {
                next = queueDelete(next, pendingOpKeyOfFile(file), file)
            }
            for (const file of result.failedUploads) {
                next = queueUpload(next, file)
            }
            return next
        })

        if (!result.indexerStarted && result.message !== "") {
            setError(result.message)
        }
    }

    const setChecked = (keys: string[], value: boolean) => {
        setCheckedKeys((current) => {
            if (value) {
                return new Set(union(Array.from(current), keys))
            }
            else {
                return new Set(difference(Array.from(current), keys))
            }
        })
    }

    const onColumnClick = (_ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        if (!column) return;
        const { key } = column;
        let isSortedDesc = isSortedDescending;

        if (sortedColumn === key) {
            isSortedDesc = !isSortedDesc;
        } else {
            isSortedDesc = false;
        }

        setSortedColumn(key);
        setIsSortedDescending(isSortedDesc);
    };

    const getSortedRows = (): FileRow[] => {
        const sorted = [...filteredRows];

        if (!sortedColumn) return sorted;

        return sorted.sort((a, b) => {
            let aValue: any;
            let bValue: any;
            let compareResult: number = 0;

            switch (sortedColumn) {
                case 'checked':
                    // 未選択(false)→選択済み(true)を昇順とする
                    aValue = Number(checkedKeys.has(a.key));
                    bValue = Number(checkedKeys.has(b.key));
                    break;
                case 'filename':
                    compareResult = naturalCollator.compare(a.key, b.key);
                    return isSortedDescending ? -compareResult : compareResult;
                case 'pending':
                    // 取りうる値が4種類しかなく同順位が多く出るため、その中はファイル名順で並べる
                    compareResult = fileRowStateOrder(a) - fileRowStateOrder(b);
                    if (compareResult === 0) {
                        compareResult = naturalCollator.compare(a.key, b.key);
                    }
                    return isSortedDescending ? -compareResult : compareResult;
                case 'size':
                    aValue = fileRowSize(a);
                    bValue = fileRowSize(b);
                    break;
                case 'type':
                    aValue = fileRowType(a);
                    bValue = fileRowType(b);
                    break;
                case 'timestamp':
                    aValue = new Date(a.file?.timestamp ?? "").getTime();
                    bValue = new Date(b.file?.timestamp ?? "").getTime();
                    break;
                case 'preprocess_name':
                    aValue = a.file?.preprocess_name || '';
                    bValue = b.file?.preprocess_name || '';
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return isSortedDescending ? 1 : -1;
            if (aValue > bValue) return isSortedDescending ? -1 : 1;
            return 0;
        });
    };

    // 全列に共通のソート用プロパティ
    const sortProps = (key: string) => ({
        isSorted: sortedColumn === key,
        isSortedDescending: isSortedDescending,
        onColumnClick: onColumnClick
    });

    const columns: IColumn[] = [
        {
            key: 'checked',
            name: '選択',
            minWidth: 40,
            maxWidth: 60,
            ...sortProps('checked'),
            onRender: (row: FileRow) => (
                <CellContent>
                    <Checkbox
                        checked={checkedKeys.has(row.key)}
                        ariaLabel={row.key}
                        disabled={!isEditable}
                        onChange={(_, checked) => {
                            setChecked([row.key], checked ?? false)
                        }}
                    />
                </CellContent>
            )
        },
        {
            key: 'filename',
            name: 'ファイル名',
            minWidth: 200,
            maxWidth: 350,
            isResizable: true,
            ...sortProps('filename'),
            onRender: (row: FileRow) => (
                <CellContent tooltip={row.key}>
                    {row.file ? (
                        <Link
                            href={getDownloadURL(row.file.container_seq, row.key)}
                            target="_blank"
                            // 削除予定のファイルは取り消し線で示す
                            style={fileRowState(row) === "delete" ? { textDecoration: "line-through" } : undefined}
                        >
                            {row.file.filename}
                        </Link>
                    ) : (
                        // まだアップロードしていないファイルはダウンロードできない
                        <span>{row.key}</span>
                    )}
                </CellContent>
            )
        },
        {
            key: 'pending',
            name: '状態',
            minWidth: 120,
            maxWidth: 220,
            isResizable: true,
            ...sortProps('pending'),
            onRender: (row: FileRow) => {
                const state = fileRowState(row)
                if (state === "none") return <CellContent>-</CellContent>

                const badge = state === "delete"
                    ? { text: "削除予定", color: "#d13438" }
                    : state === "overwrite"
                        ? { text: "上書き予定", color: "#8a6116" }
                        : { text: "追加予定", color: "#0078d4" }

                return (
                    <CellContent>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                            <span style={pendingBadgeStyle(badge.color)}>{badge.text}</span>
                            <DefaultButton
                                text="取り消し"
                                onClick={() => handleCancelPending(row.key)}
                                disabled={!isEditable}
                                styles={{ root: { height: 22, minHeight: 22, padding: '0 6px', fontSize: 11 } }}
                            />
                        </Stack>
                    </CellContent>
                )
            }
        },
        {
            key: 'size',
            name: 'サイズ',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            ...sortProps('size'),
            onRender: (row: FileRow) => (
                <CellContent>{formatFileSize(fileRowSize(row))}</CellContent>
            )
        },
        {
            key: 'type',
            name: '種別',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            ...sortProps('type'),
            onRender: (row: FileRow) => (
                <CellContent>{fileRowType(row)}</CellContent>
            )
        },
        {
            key: 'preprocess_name',
            name: '整形処理',
            minWidth: 80,
            maxWidth: 300,
            isResizable: true,
            ...sortProps('preprocess_name'),
            onRender: (row: FileRow) => (
                <CellContent tooltip={row.file?.preprocess_name ?? "-"}>
                    {
                        row.file?.preprocess_name ?
                        <Link href={getPreprocessDocumentsDownloadURL(row.file.container_seq, row.key)} target="_blank">
                            {row.file.preprocess_name}
                        </Link>
                        :
                        "-"
                    }
                </CellContent>
            )
        },
        {
            key: 'timestamp',
            name: 'アップロード日時',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            ...sortProps('timestamp'),
            onRender: (row: FileRow) => (
                <CellContent>{formatDateTime(row.file?.timestamp)}</CellContent>
            )
        },
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner
                    size={SpinnerSize.large}
                    label={
                        loadProgress && 0 < loadProgress.total
                            ? `読み込み中... (${loadProgress.loaded}/${loadProgress.total}件)`
                            : "読み込み中..."
                    }
                />
            </Stack>
        );
    }

    const { deletions, uploads } = buildApplyPlan(pendingOps)

    return (
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '20px' } }}>
            <AdminLabel text={`ファイル一覧・編集　＜${container?.container_name}＞`} />

            <IndexerLockMessageBar isIndexerRunning={isIndexerRunning} />

            {
                <ErrorDialog
                    content={error ?? ""}
                    onDismiss={() => setError(null)}
                    isOpen={Boolean(error)} />
            }

            {
                <ConfirmDialog
                    content={successMessage ?? ""}
                    onDismiss={() => setSuccessMessage(null)}
                    isOpen={Boolean(successMessage)}
                    title="確認" />
            }

            {/* 編集を完了 */}
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                <PrimaryButton
                    onClick={() => setApplying(true)}
                    disabled={!canApply || !isEditable}
                >
                    編集を完了して検索へ反映
                </PrimaryButton>
                {0 < pendingOps.size && (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        <Icon iconName="Info" styles={{ root: { color: '#605e5c', fontSize: 14 } }} />
                        <Text variant="small">
                            未実行の変更があります（アップロード{uploads.length}件 / 削除{deletions.length}件）。
                            「編集を完了」を押すまで実行されません。
                        </Text>
                    </Stack>
                )}
            </Stack>

            {/* ファイルの追加・削除 */}
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                <FilePickButton
                    text="ファイルを追加"
                    iconProps={{ iconName: 'Attach' }}
                    styles={{ root: { width: 'fit-content' } }}
                    onFileChange={handleAddFile}
                    disabled={!isEditable}
                />
                <DefaultButton
                    text="選択中のファイルを削除"
                    onClick={handleDeleteClick}
                    disabled={checkedKeys.size == 0 || !isEditable}
                />
            </Stack>

            {rows.length > 0 ? (
                <>
                    <ListFilterBox
                        value={fileNameFilter}
                        onChange={setFileNameFilter}
                        placeholder="ファイル名で絞り込み"
                        filteredCount={filteredRows.length}
                        totalCount={rows.length}
                    />

                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                        <Checkbox
                            label={`表示中の${filteredRows.length}件をすべて選択`}
                            checked={allFilteredChecked}
                            indeterminate={someFilteredChecked}
                            disabled={filteredRows.length === 0 || !isEditable}
                            onChange={() => setChecked(filteredRows.map(row => row.key), !allFilteredChecked)}
                            styles={{ root: { fontWeight: 600 } }}
                        />
                        <Label styles={{ root: { color: '#666', fontWeight: 400 } }}>
                            （{checkedKeys.size} / {filteredRows.length} 件選択中）
                        </Label>
                    </Stack>

                    {filteredRows.length === 0 ? (
                        <Label>絞り込み条件に一致するファイルがありません。</Label>
                    ) : (
                        <DetailsList
                            items={getSortedRows()}
                            columns={columns}
                            getKey={(row: FileRow) => row.key}
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionMode={SelectionMode.none}
                            isHeaderVisible={true}
                        />
                    )}
                </>
            ) : (
                <Label>ファイルがありません。</Label>
            )}

            {/* 保留中の操作の実行。開くたびに状態をリセットするため、閉じている間はマウントしない */}
            {applying && containerSeqNum !== undefined && (
                <ApplyChangesModal
                    containerSeq={containerSeqNum}
                    containerName={container?.container_name ?? ""}
                    deletions={deletions}
                    uploads={uploads}
                    onFinished={handleApplyFinished}
                />
            )}
        </Stack>
    );
}
